import { yupResolver } from '@hookform/resolvers/yup';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  Divider,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Typography,
} from '@mui/material';
import { CloseCircle } from 'asset';
import { Inputs, Select } from 'components/form-control';
import yup from 'config/yup.custom';
import { OptionItem } from 'models';
import { SITTER_AC_PARAMS_REQUEST } from 'models/subscription';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
// import { useForm } from 'react-hook-form';
import { LoadingPopup } from 'components/common';
import { NotifyService, pluralString } from 'helpers';
import { addSitterACApi, changeSitterACApi } from 'services/subscription';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { setLoadingPopup } from 'store/reducers/global';
import { setSitterACListParams } from 'store/reducers/subscription';
import { GET_SITTER_AC_LIST_REQUEST } from 'store/reducers/subscription/actionType';
import { theme } from 'theme';
import { CHILD_EXPERIENCE, PETS } from './data';

interface Props {
  open: boolean;
  handleClosePopup?: any;
  onSuccess?: any;
  subject?: string;
  isAllowedChange?: boolean;
}

interface IFormInputs {
  sitters: string[];
  search: string;
  childExperience: OptionItem | null;
  pets: OptionItem | null;
  location: string;
}

const PopupAddSitter = (props: Props) => {
  const { open, handleClosePopup, subject, onSuccess, isAllowedChange } = props;
  const { sitter_added_into_ac_job_list, base_request_detail } = useAppSelector(
    (state) => state.subscriptionReducer.subscription
  );

  const { params } = sitter_added_into_ac_job_list;
  const dispatch = useAppDispatch();
  const { loadingPopup } = useAppSelector((state) => state.globalReducer);
  const onSetListParams = (params: SITTER_AC_PARAMS_REQUEST) => {
    dispatch(setSitterACListParams(params));
  };

  useEffect(() => {
    dispatch({
      type: GET_SITTER_AC_LIST_REQUEST,
      payload: {
        params: { ...params },
      },
    });
  }, [params, dispatch]);

  const schema = useMemo(() => {
    return yup.object({
      sitters: yup.mixed().notRequired(),
    });
  }, []);

  const form = useForm<IFormInputs>({
    resolver: yupResolver(schema),
    mode: 'onChange',
    defaultValues: {
      sitters: [],
      search: '',
      childExperience: null,
      pets: null,
      location: '',
    },
  });

  const { handleSubmit, reset, watch } = form;

  const onSubmit = async (data: IFormInputs) => {
    if (subject) {
      setLoadingPopup(true);

      if (!isAllowedChange) {
        await addSitterACApi({
          sitter_ids: data.sitters,
          ac_base_request_id: subject,
        })
          .then((r) => {
            onSuccess(r.data.data);
            onClose();
            NotifyService.success('Success');
          })
          .catch((e) => NotifyService.error(e))
          .finally(() => {
            setLoadingPopup(false);
          });
      } else {
        await changeSitterACApi({
          sitter_ids: data.sitters,
          ac_base_request_id: subject,
        })
          .then((r) => {
            onSuccess(r.data.data);
            onClose();
            NotifyService.success('Success');
          })
          .catch((e) => NotifyService.error(e))
          .finally(() => {
            setLoadingPopup(false);
          });
      }
    } else {
      NotifyService.error('Sitter has been added');
    }
  };

  const onClose = () => {
    reset();
    onSetListParams({
      ...params,
      search: '',
      filters: '',
    });
    handleClosePopup();
  };

  return (
    <Dialog
      open={open}
      sx={{
        '& .MuiPaper-root': {
          width: 530,
          borderRadius: 0,
        },
      }}
    >
      <Grid>
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 25px',
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontSize: '16px',
              lineHeight: '100%',
            }}
          >
            {isAllowedChange ? 'Change Sitter' : 'Add Sitter'}
          </Typography>
          <IconButton
            onClick={handleClosePopup}
            sx={{
              padding: 0,
            }}
          >
            <CloseCircle />
          </IconButton>
        </Grid>
        <Divider />
        {loadingPopup && <LoadingPopup color="primary" />}
        <Box p="23px 32px">
          <form onSubmit={handleSubmit(onSubmit)} style={{ height: '100%' }}>
            <Stack height="100%" justifyContent="space-between">
              <Stack spacing="16px">
                <Grid>
                  <Inputs
                    title="Sitter"
                    form={form}
                    name="search"
                    placeholder="Sitter name, email or phone number"
                  />
                </Grid>
                <Grid>
                  <Inputs
                    title="Location"
                    form={form}
                    name="location"
                    placeholder="Location"
                  />
                </Grid>
                <Grid>
                  <Select
                    title="Child Experience"
                    name="childExperience"
                    bindLabel="name"
                    isMulti
                    form={form}
                    filterFunc={true}
                    options={CHILD_EXPERIENCE}
                  />
                </Grid>
                <Grid>
                  <Select
                    isMulti
                    title="Pets"
                    name="pets"
                    bindLabel="name"
                    form={form}
                    filterFunc={true}
                    options={PETS}
                  />
                </Grid>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{
                    height: '30px',
                  }}
                  onClick={() => {
                    onSetListParams({
                      ...params,
                      search: watch('search'),
                      location: watch('location'),
                      filters: `${
                        watch('childExperience')?.length > 0 &&
                        !watch('childExperience')?.some(
                          (i: any) => i.id === 'All Ages'
                        )
                          ? `${watch('childExperience')
                              ?.map((i: any) => i.id)
                              .join(',')} ` || ''
                          : ''
                      }${
                        watch('pets')?.length > 0 &&
                        !watch('pets')?.some((i: any) => i.id === 'All pets')
                          ? `,${watch('pets')
                              ?.map((i: any) => i.id)
                              .join(',')} ` || ''
                          : ''
                      }`,
                    });
                  }}
                >
                  Show Matching Sitter
                </Button>
                <Divider />
                <Grid>
                  <InputLabel
                    sx={{
                      fontWeight: 500,
                      fontSize: '15px',
                      lineHeight: '100%',
                      color: '#000',
                      marginBottom: '10px',
                    }}
                  >
                    {pluralString(
                      'Sitter',
                      sitter_added_into_ac_job_list?.data?.length ?? 0
                    )}
                    &nbsp;found
                  </InputLabel>
                  <FormGroup>
                    <Stack spacing="17px" maxHeight="300px" overflow="auto">
                      {sitter_added_into_ac_job_list?.data?.map((item) => {
                        return (
                          <Stack
                            key={item.sitter_id}
                            direction="row"
                            alignItems="center"
                          >
                            <Controller
                              name={'sitters'}
                              control={form.control}
                              render={({ field: { onChange, value } }) => (
                                <Checkbox
                                  checked={value.includes(item.sitter_id)}
                                  disabled={
                                    item.sitter_id ===
                                    base_request_detail?.sitter_id
                                  }
                                  onChange={() =>
                                    onChange(
                                      value.includes(item.sitter_id)
                                        ? value.filter(
                                            (v) => v !== item.sitter_id
                                          )
                                        : [...value, item.sitter_id]
                                    )
                                  }
                                  sx={{
                                    '&.Mui-checked': {
                                      color: theme.palette.secondary.main,
                                    },
                                  }}
                                />
                              )}
                            />
                            <Avatar
                              alt={item.User.first_name}
                              src={item.profile_picture ?? ''}
                            />
                            <Stack ml="8px">
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '13px',
                                  lineHeight: '20px',
                                  letterSpacing: '0.1px',
                                }}
                              >
                                {item.User.first_name +
                                  ' ' +
                                  item.User.last_name +
                                  ' ' +
                                  `(${item.User.email})`}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '13px',
                                  lineHeight: '20px',
                                  letterSpacing: '0.1px',
                                }}
                              >
                                Rating: {item.rating.toFixed(1)}
                              </Typography>
                            </Stack>
                          </Stack>
                        );
                      })}
                    </Stack>
                  </FormGroup>
                </Grid>
              </Stack>

              <Stack
                spacing="8px"
                direction="row"
                justifyContent="space-between"
                mt="32px"
              >
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{
                    width: '233.5px',
                    height: '30px',
                  }}
                  onClick={() => {
                    reset();
                    onClose();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  sx={{
                    width: '233.5px',
                    height: '30px',
                  }}
                  type="submit"
                  disabled={watch('sitters').length === 0}
                >
                  Add
                </Button>
              </Stack>
            </Stack>
          </form>
          <Divider />
        </Box>
      </Grid>
    </Dialog>
  );
};

export default PopupAddSitter;
